/**
 * Covered-call setup wizard.
 *
 * Four steps via MUI Stepper:
 *   1. Symbol  → live quote
 *   2. Contracts → shares-needed display
 *   3. Expiration + strike (option chain table)
 *   4. Preview + confirm
 *
 * Drives the /api/bot/* endpoints and ends with POST /api/bot/create.
 * Plain useState pattern matching Trade.tsx — no react-hook-form.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, NavigateBefore, NavigateNext } from '@mui/icons-material';

import OptionChainTable, { OptionContract } from './OptionChainTable';
import PreviewSummary, { BotPreview } from './PreviewSummary';
import QuoteCard from '../Trade/QuoteCard';
import { useQuoteStream } from '../../hooks/useQuoteStream';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STEPS = ['Symbol', 'Contracts', 'Strike & Expiration', 'Confirm'];

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const Wizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ---- pre-fill from URL params (Research page "Start" button) ----------
  const prefillSymbol = searchParams.get('symbol') || '';
  const prefillStrike = searchParams.get('strike') ? parseFloat(searchParams.get('strike')!) : null;
  const prefillExpiration = searchParams.get('expiration') || '';
  const prefillContracts = searchParams.get('contracts') ? parseInt(searchParams.get('contracts')!) : 1;
  const prefillStrategy = searchParams.get('strategy') || 'COVERED_CALL';
  const prefillPutBuyStrike = searchParams.get('putBuyStrike') ? parseFloat(searchParams.get('putBuyStrike')!) : null;
  const prefillPutSellStrike = searchParams.get('putSellStrike') ? parseFloat(searchParams.get('putSellStrike')!) : null;
  const prefillCallSellStrike = searchParams.get('callSellStrike') ? parseFloat(searchParams.get('callSellStrike')!) : null;
  const prefillCallBuyStrike = searchParams.get('callBuyStrike') ? parseFloat(searchParams.get('callBuyStrike')!) : null;
  const hasPrefill = !!(prefillSymbol && prefillStrike && prefillExpiration);

  // ---- form state -------------------------------------------------------
  const [activeStep, setActiveStep] = useState(hasPrefill ? 3 : 0);
  const [symbol, setSymbol] = useState(prefillSymbol);
  const [contracts, setContracts] = useState(prefillContracts);
  const [expiration, setExpiration] = useState<string>(prefillExpiration);
  const [selectedStrike, setSelectedStrike] = useState<number | null>(prefillStrike);
  const [selectedContract, setSelectedContract] = useState<OptionContract | null>(null);
  const [optionOrderType, setOptionOrderType] = useState<'MARKET' | 'LIMIT'>('MARKET');
  const [optionLimitPrice, setOptionLimitPrice] = useState<number | null>(null);

  // ---- data state -------------------------------------------------------
  const [debouncedSymbol, setDebouncedSymbol] = useState<string | null>(null);
  const { quote, status: quoteStatus } = useQuoteStream(debouncedSymbol);

  const [expirations, setExpirations] = useState<string[]>([]);
  const [chain, setChain] = useState<OptionContract[]>([]);
  const [chainLoading, setChainLoading] = useState(false);

  const [preview, setPreview] = useState<BotPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---- debounce symbol → live quote -------------------------------------
  // If pre-filled from Research, set debouncedSymbol immediately so the
  // preview fetches on mount instead of waiting for a 350ms debounce.
  useEffect(() => {
    if (hasPrefill && prefillSymbol) {
      setDebouncedSymbol(prefillSymbol.toUpperCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const trimmed = symbol.trim().toUpperCase();
    if (!trimmed) {
      setDebouncedSymbol(null);
      return;
    }
    const t = setTimeout(() => setDebouncedSymbol(trimmed), 350);
    return () => clearTimeout(t);
  }, [symbol]);

  const livePrice = useMemo(
    () => quote?.price ?? quote?.last ?? null,
    [quote],
  );
  const sharesNeeded = useMemo(() => {
    // We don't have local broker holdings on this screen, so assume 0.
    // The server's preview computes the real number; this is just an estimate
    // for the contracts step.
    return contracts * 100;
  }, [contracts]);
  const estimatedBuyCost = useMemo(
    () => (livePrice ? sharesNeeded * livePrice : null),
    [sharesNeeded, livePrice],
  );

  // ---- fetch expirations when entering step 3 ---------------------------
  useEffect(() => {
    if (activeStep !== 2 || !debouncedSymbol) return;
    let cancelled = false;
    (async () => {
      try {
        // Iron condors can be 0DTE — include today's expiry in the list.
        const includeToday = prefillStrategy === 'IRON_CONDOR';
        const r = await fetch(
          `${API_BASE}/api/bot/expirations?symbol=${encodeURIComponent(debouncedSymbol)}` +
            (includeToday ? '&include_today=true' : ''),
          { headers: authHeaders() },
        );
        const j = await r.json();
        if (cancelled) return;
        setExpirations(j.expirations || []);
        if (j.expirations && j.expirations.length && !expiration) {
          setExpiration(j.expirations[0]);
        }
      } catch (err) {
        console.error('expirations fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, debouncedSymbol]);

  // ---- fetch option chain when expiration changes -----------------------
  useEffect(() => {
    if (!debouncedSymbol || !expiration) return;
    let cancelled = false;
    setChainLoading(true);
    (async () => {
      try {
        const optType = prefillStrategy === 'WHEEL' ? 'put' : 'call';
        const r = await fetch(
          `${API_BASE}/api/bot/option-chain?symbol=${encodeURIComponent(
            debouncedSymbol,
          )}&expiration=${expiration}&option_type=${optType}`,
          { headers: authHeaders() },
        );
        const j = await r.json();
        if (cancelled) return;
        setChain(j.contracts || []);
      } catch (err) {
        console.error('chain fetch failed', err);
      } finally {
        if (!cancelled) setChainLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSymbol, expiration, prefillStrategy]);

  // ---- fetch preview when entering step 4 -------------------------------
  useEffect(() => {
    if (activeStep !== 3) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    if (!debouncedSymbol || !expiration || selectedStrike === null) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/bot/preview`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            strategy: prefillStrategy,
            symbol: debouncedSymbol,
            contracts,
            expiration,
            strike: selectedStrike,
            ...(prefillStrategy === 'IRON_CONDOR' ? {
              putBuyStrike: prefillPutBuyStrike,
              putSellStrike: prefillPutSellStrike,
              callSellStrike: prefillCallSellStrike,
              callBuyStrike: prefillCallBuyStrike,
            } : {}),
          }),
        });
        const j = await r.json();
        if (cancelled) return;
        if (!r.ok || !j.success) {
          setPreviewError(j.detail || j.message || 'Preview failed');
          setPreview(null);
        } else {
          setPreview(j.preview);
        }
      } catch (err: any) {
        if (!cancelled) setPreviewError(err.message || 'Network error');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeStep, debouncedSymbol, expiration, selectedStrike, contracts]);

  // ---- step navigation guards -------------------------------------------
  const canAdvance = useMemo(() => {
    if (activeStep === 0) return !!debouncedSymbol && quoteStatus !== 'error';
    if (activeStep === 1) return contracts > 0;
    if (activeStep === 2)
      return (
        !!expiration &&
        selectedStrike !== null &&
        (optionOrderType === 'MARKET' ||
          (optionOrderType === 'LIMIT' && optionLimitPrice !== null && optionLimitPrice > 0))
      );
    return true;
  }, [activeStep, debouncedSymbol, quoteStatus, contracts, expiration, selectedStrike, optionOrderType, optionLimitPrice]);

  const handleNext = () => setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  // ---- submit -----------------------------------------------------------
  const handleConfirm = async () => {
    if (!debouncedSymbol || !expiration || selectedStrike === null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await fetch(`${API_BASE}/api/bot/create`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          strategy: prefillStrategy,
          symbol: debouncedSymbol,
          contracts,
          expiration,
          strike: selectedStrike,
          optionOrderType: optionOrderType,
          ...(optionOrderType === 'LIMIT' && optionLimitPrice
            ? { optionLimitPrice }
            : {}),
          ...(prefillStrategy === 'IRON_CONDOR' ? {
            putBuyStrike: prefillPutBuyStrike,
            putSellStrike: prefillPutSellStrike,
            callSellStrike: prefillCallSellStrike,
            callBuyStrike: prefillCallBuyStrike,
          } : {}),
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) {
        throw new Error(j.detail || j.message || 'Bot creation failed');
      }
      // Done — head to the Robot page where the new bot will appear.
      navigate('/robot');
    } catch (err: any) {
      setSubmitError(err.message || 'Network error');
    } finally {
      setSubmitting(false);
      setConfirmDialog(false);
    }
  };

  // ---- step renderers ---------------------------------------------------
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Pick a stock
                  </Typography>
                  <TextField
                    fullWidth
                    label="Symbol"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    placeholder="e.g. AAPL"
                    autoFocus
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Type a ticker — the live quote on the right updates as you type.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <QuoteCard
                symbol={debouncedSymbol}
                quote={quote}
                status={quoteStatus}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    How many contracts?
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    label="Contracts"
                    value={contracts}
                    onChange={(e) => setContracts(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1, step: 1 }}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Each contract requires <strong>100 shares</strong> of the underlying.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${contracts * 100} shares needed`} />
                      {estimatedBuyCost !== null && (
                        <Chip
                          label={`≈ $${estimatedBuyCost.toLocaleString('en-US', { maximumFractionDigits: 2 })}`}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      Server will skip the buy if you already hold enough shares.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <QuoteCard
                symbol={debouncedSymbol}
                quote={quote}
                status={quoteStatus}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Expiration
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Expiration</InputLabel>
                    <Select
                      value={expiration}
                      label="Expiration"
                      onChange={(e) => {
                        setExpiration(e.target.value);
                        setSelectedStrike(null);
                        setSelectedContract(null);
                      }}
                    >
                      {expirations.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 2, display: 'block' }}
                  >
                    The strategy's auto-rolls pick the nearest expiration ≥3 days
                    out. Pick something in that range now.
                  </Typography>
                  {selectedContract && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        SELECTED
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedContract.symbol}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Order Type
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Order Type</InputLabel>
                      <Select
                        value={optionOrderType}
                        label="Order Type"
                        onChange={(e) => {
                          const v = e.target.value as 'MARKET' | 'LIMIT';
                          setOptionOrderType(v);
                          if (v === 'MARKET') setOptionLimitPrice(null);
                        }}
                      >
                        <MenuItem value="MARKET">Market</MenuItem>
                        <MenuItem value="LIMIT">Limit</MenuItem>
                      </Select>
                    </FormControl>
                    {optionOrderType === 'LIMIT' && (
                      <TextField
                        fullWidth
                        type="number"
                        label="Limit Price"
                        value={optionLimitPrice ?? ''}
                        onChange={(e) =>
                          setOptionLimitPrice(
                            e.target.value ? parseFloat(e.target.value) : null,
                          )
                        }
                        inputProps={{ min: 0.01, step: 0.01 }}
                        helperText={
                          selectedContract
                            ? `Bid: $${selectedContract.bid?.toFixed(2) ?? '—'} / Ask: $${selectedContract.ask?.toFixed(2) ?? '—'}`
                            : 'Select a strike first'
                        }
                      />
                    )}
                    {optionOrderType === 'MARKET' && (
                      <Typography variant="caption" color="text.secondary">
                        Market orders are only accepted during regular market hours (9:30 AM – 4:00 PM ET).
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {prefillStrategy === 'WHEEL' ? 'Puts' : 'Calls'} — pick a strike
                  </Typography>
                  <OptionChainTable
                    contracts={chain}
                    loading={chainLoading}
                    currentPrice={livePrice}
                    selectedStrike={selectedStrike}
                    onSelect={(c) => {
                      setSelectedStrike(c.strike);
                      setSelectedContract(c);
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 7 }}>
              <PreviewSummary
                preview={preview}
                loading={previewLoading}
                error={previewError}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Ready?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Confirming will:
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                    {prefillStrategy === 'IRON_CONDOR' ? (
                      <>
                        <li><Typography variant="body2">Place a 4-leg iron condor order (bull put spread + bear call spread)</Typography></li>
                        <li><Typography variant="body2">Put spread: ${prefillPutBuyStrike}/${prefillPutSellStrike}</Typography></li>
                        <li><Typography variant="body2">Call spread: ${prefillCallSellStrike}/${prefillCallBuyStrike}</Typography></li>
                        <li><Typography variant="body2">Bot monitors and closes at 50% profit or if approaching max loss</Typography></li>
                      </>
                    ) : prefillStrategy === 'WHEEL' ? (
                      <>
                        <li><Typography variant="body2">Sell a cash-secured PUT at the chosen strike</Typography></li>
                        <li><Typography variant="body2">If assigned, auto-switch to selling covered calls</Typography></li>
                        <li><Typography variant="body2">If called away, auto-switch back to selling puts (continuous cycle)</Typography></li>
                      </>
                    ) : (
                      <>
                        <li><Typography variant="body2">Place a market BUY for any shares you don't already hold</Typography></li>
                        <li><Typography variant="body2">Once the buy fills, place a SELL_TO_OPEN for the call</Typography></li>
                        <li><Typography variant="body2">Create a Bot that auto-rolls on expiry/assignment</Typography></li>
                      </>
                    )}
                  </Box>
                  {submitError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {submitError}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={() => setConfirmDialog(true)}
                    disabled={!preview || submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
                  >
                    {submitting ? 'Creating bot…' : 'Confirm & Create Bot'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
        {prefillStrategy === 'IRON_CONDOR' ? 'New Iron Condor' : prefillStrategy === 'WHEEL' ? 'New Wheel Strategy' : 'New Covered Call'}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStep()}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<NavigateBefore />}
        >
          Back
        </Button>
        {activeStep < STEPS.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canAdvance}
            endIcon={<NavigateNext />}
          >
            Next
          </Button>
        )}
      </Box>

      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {prefillStrategy === 'IRON_CONDOR' ? 'Confirm Iron Condor' : prefillStrategy === 'WHEEL' ? 'Confirm Wheel Strategy' : 'Confirm Covered Call'}
        </DialogTitle>
        <DialogContent>
          {preview && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You're about to create a{' '}
                <strong>
                  {prefillStrategy === 'IRON_CONDOR' ? 'iron condor' : prefillStrategy === 'WHEEL' ? 'wheel' : 'covered call'}
                </strong>{' '}
                bot for <strong>{preview.symbol}</strong>.
              </Typography>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                {prefillStrategy === 'IRON_CONDOR' ? (
                  <>
                    <Typography variant="body2">
                      <strong>{preview.contracts}× {preview.symbol} Iron Condor</strong>
                    </Typography>
                    <Typography variant="body2">
                      Put spread: <strong>${prefillPutBuyStrike?.toFixed(2)} / ${prefillPutSellStrike?.toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Call spread: <strong>${prefillCallSellStrike?.toFixed(2)} / ${prefillCallBuyStrike?.toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Expires: <strong>{preview.expiration}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Credit: <strong>${(preview.totalCredit || preview.premiumTotal || 0).toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Margin required: <strong>${(preview.marginRequired || preview.netCost || 0).toFixed(2)}</strong>
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      <strong>{preview.contracts}× {preview.symbol}</strong> {prefillStrategy === 'WHEEL' ? 'put' : 'call'} @ ${(preview.strike || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2">
                      Expires: <strong>{preview.expiration}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Shares to buy: <strong>{preview.sharesNeeded || 0}</strong> (cost ≈ ${(preview.shareCost || 0).toFixed(2)})
                    </Typography>
                    <Typography variant="body2">
                      Premium: <strong>${(preview.premiumTotal || 0).toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Net out-of-pocket: <strong>${(preview.netCost || 0).toFixed(2)}</strong>
                    </Typography>
                  </>
                )}
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Orders will be placed through Alpaca paper trading.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {submitting ? 'Creating…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Wizard;
